import { Title } from "@mantine/core";
import { RegulatoryDutyCategory } from "../../../../types/api.types";
import type {
  ExtendedUser,
  Person,
  Priority,
  TodoCategory,
  TodoStatus
} from "../../../../utils/backend-types";
import { EMPTY_FIELD } from "../../../../utils/constants";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { useShouldShowStaffView } from "../../../StaffViewToggle/useShouldShowStaffView";
import { type RegulatoryDutyType } from "../../hooks/useRegulatoryDuties";
import { TodoDescription } from "../../TodoDescription/TodoDescription";
import { CATEGORIES } from "../../Todos.constants";
import { TodoDueDateCell } from "../../TodoTable/Cells/TodoDueDateCell";
import { TodoPersonCell } from "../../TodoTable/Cells/TodoPersonCell";
import { TodoResponsibleCell } from "../../TodoTable/Cells/TodoResponsibleCell";
import type { TodoStatusCellProps } from "../../TodoTable/TableColumns";
import {
  PriorityCell,
  TodoDocumentStatusCell,
  TodoStatusCell
} from "../../TodoTable/TableColumns";

interface DescriptionTabProps extends Pick<
  TodoStatusCellProps,
  "onChangeStatus"
> {
  todoId: number;
  label: string;
  description: string | null;
  regulatoryDuty: RegulatoryDutyType | null;
  category: TodoCategory;
  priorityValue: Priority;
  fulfillFor: string | null;
  personName: string | null;
  dueDate: string | null;
  overdue: boolean;
  responsibleId: number | null;
  status: TodoStatus;
  users: Array<ExtendedUser>;
  universal: boolean;
  todoStatus: boolean;
  persons: Array<Person>;
  onChangeStatus: (status: TodoStatus, todoId: number) => void;
  onChangeResponsible: (responsibleId: number | null, todoId: number) => void;
  onChangePriority: (priorityValue: Priority, todoId: number) => void;
  onChangeTodoStatus: (todoId: number) => void;
  onChangeDueDate: (todoId: number, dueDate: string | null) => void;
  onChangeOverdue: (todoId: number, overdue: boolean) => void;
  onChangePerson: (todoId: number, person: number) => void;
}

function DescriptionTab({
  todoId,
  label,
  description,
  regulatoryDuty,
  category,
  fulfillFor,
  personName,
  dueDate,
  overdue,
  responsibleId,
  priorityValue,
  status,
  users,
  universal,
  todoStatus,
  persons,
  onChangeStatus,
  onChangeResponsible,
  onChangePriority,
  onChangeTodoStatus,
  onChangeDueDate,
  onChangePerson,
  onChangeOverdue
}: DescriptionTabProps) {
  const categoryKey = Object.keys(CATEGORIES).find(
    (categoryKey) =>
      CATEGORIES[categoryKey as keyof typeof CATEGORIES].value === category
  );

  const { shouldShowStaffView } = useShouldShowStaffView();

  return (
    <div className="overview">
      <div className="todo-property">
        {!description && (
          <Title order={6}>Informationen zur regulatorischen Pflicht</Title>
        )}
        <TodoDescription
          regulatoryDuty={regulatoryDuty}
          userDefinedDescription={description}
        />
      </div>
      {categoryKey && CATEGORIES[categoryKey] && (
        <div className="todo-property">
          <Title mt="sm" order={6}>
            Kategorie
          </Title>
          <span className="todo-property-value">
            {CATEGORIES[categoryKey].displayName}
          </span>
        </div>
      )}
      {fulfillFor && (
        <div className="todo-property">
          <Title mt="sm" order={6}>
            Zu erfüllen für
          </Title>
          <span className="todo-property-value">{fulfillFor}</span>
        </div>
      )}
      <div className="todo-property">
        <Title mt="sm" order={6}>
          Verpflichtetes Unternehmen
        </Title>
        {regulatoryDuty?.category === RegulatoryDutyCategory.Premium ? (
          <TodoPersonCell
            person={personName}
            persons={persons}
            todoId={todoId}
            onChangePerson={onChangePerson}
          />
        ) : (
          <span className="todo-property-value">
            {personName || EMPTY_FIELD}
          </span>
        )}
      </div>
      <div className="todo-property">
        <Title mt="sm" order={6}>
          Fälligkeit
        </Title>
        {regulatoryDuty?.category === RegulatoryDutyCategory.Premium ? (
          <TodoDueDateCell
            dueDate={
              dueDate
                ? backendDateOrDateTimeToLuxonDateTime(dueDate)
                : undefined
            }
            overdue={overdue}
            todoId={todoId}
            onChangeDueDate={onChangeDueDate}
            onChangeOverdue={onChangeOverdue}
          />
        ) : (
          <span className="todo-property-value">
            {overdue ? "Überfällig" : dueDate || EMPTY_FIELD}
          </span>
        )}
      </div>
      <div className="todo-property">
        <Title mt="sm" order={6}>
          Verantwortlich
        </Title>
        <TodoResponsibleCell
          responsibleId={responsibleId}
          todoId={todoId}
          universal={universal}
          users={users}
          onChangeResponsible={onChangeResponsible}
        />
      </div>
      <div className="todo-property">
        <Title mt="sm" order={6}>
          Priorität
        </Title>
        <PriorityCell
          id={todoId}
          priorityValue={priorityValue}
          onChangePriority={onChangePriority}
        />
      </div>

      <div className="todo-property">
        <Title mt="sm" order={6}>
          Status
        </Title>
        <TodoStatusCell
          id={todoId}
          label={label}
          status={status}
          onChangeStatus={onChangeStatus}
        />
      </div>
      <div className="todo-property">
        <Title mt="sm" order={6}>
          Aufgaben Status
        </Title>
        <TodoDocumentStatusCell
          id={todoId}
          isStaff={shouldShowStaffView}
          status={todoStatus}
          onChangeTodoStatus={onChangeTodoStatus}
        />
      </div>
    </div>
  );
}

export { DescriptionTab, DescriptionTabProps };
